/**
 * Utility functions for IPUA and Adobe Analytics environment detection
 * Extracted for testability from adobe-script.jsx
 */

/**
 * IPUA environment mapping:
 * - dev.exemptions.avalara.io → qa (DEV uses QA IPUA)
 * - qa.exemptions.avalara.io → qa
 * - sbx.exemptions.avalara.io → sbx
 * - exemptions.avalara.io (prd) → prd
 * - localhost → qa
 *
 * @param hostname - The current window.location.hostname
 * @param fallbackEnv - Fallback environment if hostname doesn't match any pattern (default: "qa")
 * @returns The IPUA environment string
 */
export function getIpuaEnvFromHostname(hostname: string, fallbackEnv: string = "qa"): string {
    // Local development
    if (hostname === "localhost" || hostname === "127.0.0.1") {
        return "qa";
    }

    // Production (no environment prefix)
    if (hostname === "exemptions.avalara.io") {
        return "prd";
    }

    // Extract environment from hostname pattern: {env}.exemptions.avalara.io
    const match = hostname.match(/^(dev|qa|sbx|prd)\.exemptions\.avalara\.io$/);
    if (match) {
        const env = match[1];
        // DEV and QA both use 'qa' for IPUA
        return env === "dev" ? "qa" : env;
    }

    // Fallback for unknown hostnames
    return fallbackEnv;
}

/**
 * Adobe Analytics script URLs
 */
export const ADOBE_ANALYTICS_PROD_SCRIPT =
    "//assets.adobedtm.com/32ec36485c31/372aeb24c16d/launch-2a732ddc79bb.min.js";
export const ADOBE_ANALYTICS_DEV_SCRIPT =
    "//assets.adobedtm.com/32ec36485c31/372aeb24c16d/launch-6875737eb25e-development.min.js";

/**
 * Determine which Adobe Analytics script to use based on hostname
 * PRD uses production script, all other environments use development script
 *
 * @param hostname - The current window.location.hostname
 * @returns The Adobe Analytics script URL
 */
export function getAdobeAnalyticsScript(hostname: string): string {
    const isProd = hostname === "exemptions.avalara.io";
    return isProd ? ADOBE_ANALYTICS_PROD_SCRIPT : ADOBE_ANALYTICS_DEV_SCRIPT;
}
